import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Stack, Typography, Chip } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { useLocation } from 'react-router-dom';
import { DistributionCenterInventoryHistory } from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistory';
import DistributionCenterInventoryHistoryService from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistoryCtrl';
import { DataGrid, esES, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

const InventoryHistoryDetailsScreen: React.FunctionComponent = () => {
    const location = useLocation();
    const distributionCenterInventoryHistoryService = useMemo(
        () => new DistributionCenterInventoryHistoryService(),
        []
    );
    const [inventoryHistory, setInventoryHistory] = useState<DistributionCenterInventoryHistory | undefined>(undefined);

    const getTotal = () => {
        return inventoryHistory?.inputs.reduce((a, item) => a + item.quantity * item.input.purchaseCost, 0).toFixed(2);
    };

    const getData = async (distributionCenterInventoryHistoryId: string) => {
        showLoader();
        try {
            const inventoryHistoryData = await distributionCenterInventoryHistoryService.getInventoryHistory(
                distributionCenterInventoryHistoryId
            );
            setInventoryHistory(inventoryHistoryData);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        const state = location.state as { distributionCenterInventoryHistoryId: string } | undefined;
        const distributionCenterInventoryHistoryId = state?.distributionCenterInventoryHistoryId;
        if (distributionCenterInventoryHistoryId) {
            void getData(distributionCenterInventoryHistoryId);
        }
    }, [location.state]);

    const columns = useMemo<GridColDef<any>[]>(
        () => [
            {
                field: 'name',
                flex: 3,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.input.name}</Typography>
            },
            {
                field: 'quantity',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cantidad
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.quantity}</Typography>
            },
            {
                field: 'cost',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo unitario
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost}`}</Typography>
            },
            {
                field: 'total',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost * row.quantity}`}</Typography>
            }
        ],
        []
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} borderRadius={4} p={4} height="100%" spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h5" fontWeight={600}>
                            Detalle de Movimiento
                        </Typography>
                        {inventoryHistory && (
                            <Chip
                                label={inventoryHistory.movementType === 'input' ? 'Entrada' : 'Salida'}
                                color={inventoryHistory.movementType === 'input' ? 'success' : 'error'}
                            />
                        )}
                    </Stack>
                </Stack>
                <Stack>
                    <Stack direction="row" alignItems="center" spacing={4}>
                        <Stack direction="column">
                            <Typography>Monto Total</Typography>
                            <Typography fontWeight={600}>{`$${getTotal()}`}</Typography>
                        </Stack>
                        <Stack direction="column">
                            <Typography>Fecha</Typography>
                            <Typography fontWeight={600}>
                                {inventoryHistory && inventoryHistory.date
                                    ? format(inventoryHistory.date.toDate(), 'dd-MM-yyyy HH:mm')
                                    : 'N/A'}
                            </Typography>
                        </Stack>
                        <Stack direction="column">
                            <Typography>Registrado por</Typography>
                            <Typography fontWeight={600}>
                                {`${inventoryHistory?.userData?.profile?.name || 'Desconocido'} (${
                                    inventoryHistory?.userData?.profile?.profile || 'N/A'
                                })`}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <DataGrid
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    sx={{ borderWidth: 0 }}
                    getRowId={(row) => `${row.id}-${row.input.id}`}
                    rows={inventoryHistory?.inputs || []}
                    columns={columns}
                />
            </Stack>
        </Box>
    );
};
export default InventoryHistoryDetailsScreen;
